// communityProfile/styled.js
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  FormControl,
  Select,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';

// Import honey colors from your existing styled components
export const honeyColors = {
  primary: '#F1C40F',
  primaryLight: '#F4D03F',
  primaryDark: '#D4AC0D',
  amber: '#FFB74D',
  cream: '#FFF8E1',
  honeycomb: '#D4A574',
  caramel: '#8D6E63',
  success: '#66BB6A',
  warning: '#FF9800',
  info: '#29B6F6',
  error: '#EF5350',
  background: '#FFFEF7',
  surface: '#FFFFFF',
  surfaceHoney: '#FFF9E6',
  border: '#F5E6B3',
  textPrimary: '#3E2723',
  textSecondary: '#5D4037',
  textMuted: '#8D6E63',
  gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
  gradientHoneycomb: 'linear-gradient(135deg, #D4A574 0%, #F1C40F 50%, #FFB74D 100%)',
  gradientGlass: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
  shadowLight: '0 2px 8px rgba(212, 165, 116, 0.15)',
  shadowMedium: '0 4px 16px rgba(212, 165, 116, 0.25)',
  shadowHeavy: '0 8px 32px rgba(212, 165, 116, 0.35)',
  shadowGlow: '0 0 20px rgba(241, 196, 15, 0.3)',
};

export const ProfileContainer = styled(Box)({
  padding: '32px',
  backgroundColor: honeyColors.background,
  minHeight: '100vh',
  maxWidth: '800px',
  margin: '0 auto',
});

export const ProfileHeader = styled(Box)({
  marginBottom: '32px',
  padding: '24px',
  backgroundColor: honeyColors.surface,
  borderRadius: '20px',
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: honeyColors.gradientPrimary,
  },
});

export const ProfileTitle = styled(Typography)({
  fontSize: '1.8rem !important',
  fontWeight: '800 !important',
  color: honeyColors.textPrimary,
  marginBottom: '8px !important',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  
  '& .MuiSvgIcon-root': {
    color: honeyColors.primary,
    fontSize: '1.8rem',
  },
});

export const ProfileSubtitle = styled(Typography)({
  color: honeyColors.textSecondary,
  fontSize: '1rem !important',
  fontWeight: '500 !important',
});

export const SectionCard = styled(Card)({
  marginBottom: '24px',
  backgroundColor: honeyColors.surface,
  borderRadius: '20px',
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: honeyColors.gradientPrimary,
  },
  
  '&:hover': {
    borderColor: honeyColors.primary,
    boxShadow: honeyColors.shadowMedium,
    transform: 'translateY(-2px)',
  },
});

export const SectionCardContent = styled(CardContent)({
  padding: '32px !important',
});

export const SectionTitle = styled(Typography)({
  fontSize: '1.4rem !important',
  fontWeight: '700 !important',
  color: honeyColors.textPrimary,
  marginBottom: '20px !important',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  
  '& .MuiSvgIcon-root': {
    color: honeyColors.primary,
    fontSize: '1.4rem',
  },
});

export const StatusBadge = styled(Box)(({ status, role }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'AC': return honeyColors.success;
      case 'PP': return honeyColors.warning;
      case 'IN': return honeyColors.textMuted;
      case 'RE': return honeyColors.error;
      case 'BA': return honeyColors.error;
      default: return honeyColors.textMuted;
    }
  };
  
  const getRoleColor = () => {
    switch (role) {
      case 'FO': return honeyColors.primary;
      case 'AD': return honeyColors.info;
      case 'MA': return honeyColors.amber;
      case 'PA': return honeyColors.success;
      case 'GU': return honeyColors.textMuted;
      default: return honeyColors.textMuted;
    }
  };
  
  const color = status ? getStatusColor() : getRoleColor();
  
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '12px',
    backgroundColor: honeyColors.surfaceHoney,
    border: `2px solid ${color}`,
    color: color,
    fontWeight: 600,
    fontSize: '0.9rem',
    
    '&::before': {
      content: '""',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: color,
    },
  };
});

export const InfoGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '20px',
  marginBottom: '24px',
});

export const InfoItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const InfoLabel = styled(Typography)({
  color: honeyColors.textSecondary,
  fontWeight: '600 !important',
  fontSize: '0.9rem !important',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

export const InfoValue = styled(Typography)({
  color: honeyColors.textPrimary,
  fontWeight: '500 !important',
  fontSize: '1rem !important',
});

export const StyledFormControl = styled(FormControl)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: honeyColors.surfaceHoney,
    border: `2px solid ${honeyColors.border}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&:hover': {
      borderColor: honeyColors.primary,
      backgroundColor: honeyColors.surface,
    },
    
    '&.Mui-focused': {
      borderColor: honeyColors.primary,
      backgroundColor: honeyColors.surface,
      boxShadow: honeyColors.shadowGlow,
    },
    
    '& fieldset': {
      border: 'none',
    },
  },
  
  '& .MuiInputLabel-root': {
    color: honeyColors.textSecondary,
    fontWeight: 600,
    
    '&.Mui-focused': {
      color: honeyColors.primary,
    },
  },
});

export const StyledSelect = styled(Select)({
  borderRadius: '12px !important',
});

export const ActionButton = styled(Button)(({ variant }) => {
  const getButtonStyles = () => {
    switch (variant) {
      case 'save':
        return {
          background: honeyColors.gradientPrimary,
          color: honeyColors.textPrimary,
          border: `2px solid ${honeyColors.border}`,
          '&:hover': {
            background: honeyColors.gradientHoneycomb,
            borderColor: honeyColors.primary,
          },
        };
      case 'danger':
        return {
          backgroundColor: 'transparent',
          color: honeyColors.error,
          border: `2px solid ${honeyColors.error}`,
          '&:hover': {
            backgroundColor: honeyColors.error,
            color: 'white',
          },
        };
      case 'report':
        return {
          backgroundColor: 'transparent',
          color: honeyColors.warning,
          border: `2px solid ${honeyColors.warning}`,
          '&:hover': {
            backgroundColor: honeyColors.warning,
            color: 'white',
          },
        };
      default:
        return {
          backgroundColor: 'transparent',
          color: honeyColors.textSecondary,
          border: `2px solid ${honeyColors.border}`,
          '&:hover': {
            backgroundColor: honeyColors.surfaceHoney,
            borderColor: honeyColors.primary,
            color: honeyColors.textPrimary,
          },
        };
    }
  };
  
  return {
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    padding: '12px 24px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: honeyColors.shadowLight,
    ...getButtonStyles(),
    
    '&:hover': {
      ...getButtonStyles()['&:hover'],
      transform: 'translateY(-2px)',
      boxShadow: honeyColors.shadowMedium,
    },
  };
});

export const ActionButtonsContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  justifyContent: 'space-between',
  marginTop: '32px',
  paddingTop: '24px',
  borderTop: `2px solid ${honeyColors.border}`,
  flexWrap: 'wrap',
});

export const VisibilityOption = styled(Box)({
  padding: '16px',
  borderRadius: '12px',
  border: `2px solid ${honeyColors.border}`,
  backgroundColor: honeyColors.surfaceHoney,
  marginBottom: '12px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  
  '&:hover': {
    borderColor: honeyColors.primary,
    backgroundColor: honeyColors.surface,
  },
  
  '&.selected': {
    borderColor: honeyColors.primary,
    backgroundColor: honeyColors.surface,
    boxShadow: honeyColors.shadowGlow,
  },
});

export const VisibilityTitle = styled(Typography)({
  fontWeight: '600 !important',
  color: honeyColors.textPrimary,
  marginBottom: '4px !important',
});

export const VisibilityDescription = styled(Typography)({
  color: honeyColors.textSecondary,
  fontSize: '0.9rem !important',
});

export const AdmissionProgress = styled(Box)({
  marginTop: '16px',
  padding: '16px',
  backgroundColor: honeyColors.surfaceHoney,
  borderRadius: '12px',
  border: `2px solid ${honeyColors.border}`,
});

export const ProgressLabel = styled(Typography)({
  color: honeyColors.textSecondary,
  fontWeight: '600 !important',
  fontSize: '0.9rem !important',
  marginBottom: '8px !important',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const StyledLinearProgress = styled(LinearProgress)({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: honeyColors.border,
  
  '& .MuiLinearProgress-bar': {
    backgroundColor: honeyColors.primary,
    borderRadius: '4px',
  },
});

export const CommunityBanner = styled(Box)({
  width: '100%',
  height: '120px',
  borderRadius: '16px',
  overflow: 'hidden',
  marginBottom: '16px',
  border: `2px solid ${honeyColors.border}`,
  position: 'relative',
  
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50%',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.3))',
  },
});

export const CommunityInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const CommunityAvatar = styled(Box)({
  width: '60px',
  height: '60px',
  borderRadius: '12px',
  overflow: 'hidden',
  border: `2px solid ${honeyColors.border}`,
  flexShrink: 0,
  
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

export const CommunityDetails = styled(Box)({
  flex: 1,
});

export const CommunityName = styled(Typography)({
  fontWeight: '700 !important',
  color: honeyColors.textPrimary,
  fontSize: '1.2rem !important',
  marginBottom: '4px !important',
});

export const CommunityMeta = styled(Typography)({
  color: honeyColors.textSecondary,
  fontSize: '0.9rem !important',
});